// =============================================================================
// ui/tabs/RecapEditor.tsx — the EOD recap, editable.
// -----------------------------------------------------------------------------
// The first journal write surface. Six prose fields and one attestation, and
// nothing else — the cut line is not timidity, it is the only place where the
// write can be made safe today. See `io/sync/saveJournalRecap.ts`: everything
// outside these fields lives in JSONB the typed model cannot round-trip, so an
// editor for it would destroy four self-assessment ratings and every
// hypothesis→pattern link on save.
//
// ── 🚨 THE LABELS ARE CROSSED RELATIVE TO THE COLUMN NAMES, ON PURPOSE ──
//
// "Things I did well" edits `eodRecap.wins` and "Things to fix" edits
// `eodRecap.mistakes`, which is what the names say — but the DATABASE columns
// `eod_wins` / `eod_mistakes` held each other's content for years (D60) and the
// read undoes that. Nothing here may touch a raw column, and nothing here may
// re-invert: the entry in memory is already correct.
//
// ── 🚨 THE ATTESTATION IS THREE-STATE, AND A CHECKBOX WOULD BE A LIE ──
//
// `preMarketPlanFollowed` is read by the old client's glance band as
// `true → Yes`, `false → No`, `undefined → —`. So a plain checkbox, unchecked by
// default, would write `false` — "I did not follow my plan" — for any day the user
// saved without touching it. That is a claim they never made, displayed as if they
// had. Radios with an explicit "not stated" is the honest control.
//
// Note this is the OPPOSITE of the trade breach flags, where an unchecked box is
// deleted rather than stored as false. Same-looking widget, inverted rule, because
// the two fields are read differently.
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAutosave } from '../state/useAutosave';
import { useJournalHidden } from '../state/useJournalHidden';
import { useSupabase } from '../state/supabase';
import { saveJournalRecap, type RecapPatch } from '../../io/sync/saveJournalRecap';
import { appendHypothesis, offerableHypotheses } from '../../selectors/journal';
import type { JournalEntry } from '../../selectors/journal';

/** The one boolean this surface owns. */
const FOLLOWED = 'preMarketPlanFollowed';

/**
 * ⚠ LABELS TRANSCRIBED FROM THE OLD CLIENT, not invented.
 *
 * The first version used "What worked" / "What broke", which is wrong in a way a
 * source read cannot catch and a screenshot comparison catches instantly:
 * `eod_what_broke` is the old client's **"Failed setups (valid but didn't play
 * out)"** — a setup that was sound and simply did not work. "What broke" reads as
 * "what I did wrong", which is the NEXT field down. Two adjacent fields, one
 * meaning swapped between them.
 */
/**
 * ⚠ THE ORDER IS THE LAYOUT. The first four pair up 2×2 — positive in the left
 * column, negative in the right — exactly as the old client renders them.
 * `wide` spans both columns, which is how Lessons and Tomorrow's Focus sit there.
 */
const FIELDS: readonly {
  key: keyof RecapPatch; label: string; rows: number; wide?: boolean; focus?: boolean;
  /** Which grid column this field is pinned to. Omitted for the wide ones. */
  col?: 'l' | 'r';
  /**
   * The field this one sits beside (TCR-83). When the partner is hidden this
   * field spans both columns instead of leaving a hole — and ONLY then, because
   * the pinned columns above are what stop a survivor drifting into the other
   * half while both are on screen.
   */
  pair?: keyof RecapPatch;
  /**
   * Offer this morning's hypotheses above the box (TCR-88). Josh: *"Both
   * Successful and Failed setup sections should include the ability to choose
   * from the hypos created in the premarket plan. The free text field should
   * also remain in case hypos were not written."*
   *
   * ⚠ THESE TWO FIELDS ONLY. The other four are not about setups — offering a
   * hypothesis under "Things I need to fix" would invite the reader to file the
   * morning's idea as a mistake.
   */
  hypotheses?: true;
}[] = [
  { key: 'whatWorked', label: 'Successful setups', rows: 3, col: 'l', pair: 'whatBroke', hypotheses: true },
  { key: 'whatBroke', label: 'Failed setups (valid but didn’t play out)', rows: 3, col: 'r', pair: 'whatWorked', hypotheses: true },
  { key: 'wins', label: 'Things I did well', rows: 3, col: 'l', pair: 'mistakes' },
  { key: 'mistakes', label: 'Things I need to fix', rows: 3, col: 'r', pair: 'wins' },
  { key: 'lessons', label: 'Lessons', rows: 2, wide: true },
  // ⚠ `focus` GIVES IT THE AMBER PILL, and it is the only highlighted label on
  // the form. Beta marks it because it is the one box that leaves this screen —
  // it is carried onto the next day's Pre-Market as "Today's Focus". Josh's
  // side-by-side showed it plain here and highlighted there.
  { key: 'tomorrowFocus', label: 'Tomorrow’s focus', rows: 2, wide: true, focus: true },
];

const patchOf = (e: JournalEntry): RecapPatch => ({
  whatWorked: e.eodRecap.whatWorked,
  whatBroke: e.eodRecap.whatBroke,
  mistakes: e.eodRecap.mistakes,
  wins: e.eodRecap.wins,
  lessons: e.eodRecap.lessons,
  tomorrowFocus: e.eodRecap.tomorrowFocus,
});

/** `true` / `false` / `null` for "not stated" — never coerced to a boolean. */
type Followed = boolean | null;

const followedOf = (e: JournalEntry): Followed => {
  const v = e.booleans[FOLLOWED];
  return v === true ? true : v === false ? false : null;
};

export interface RecapEditorProps {
  entry: JournalEntry;
  date: string;
  /** From `useJournalTab`. Null means "never loaded", not "no conflict". */
  baselineUpdatedAt: string | null;
  onSaved: (entry: JournalEntry, updatedAt: string | null) => void;
}

export function RecapEditor({ entry, date, baselineUpdatedAt, onSaved }: RecapEditorProps) {
  const { sb, userId } = useSupabase();

  // ⚠ Seeded from the entry, and the entry came from a full-row read. There is no
  // projected-row hazard here as there is for trades — `loadJournal` selects every
  // column this surface shows — but the same rule applies: a field is editable only
  // because it was read.
  const [draft, setDraft] = useState<RecapPatch>(() => patchOf(entry));
  const [followed, setFollowed] = useState<Followed>(() => followedOf(entry));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const stored = useMemo(() => patchOf(entry), [entry]);
  const storedFollowed = useMemo(() => followedOf(entry), [entry]);
  const dirty = useMemo(
    () => followed !== storedFollowed
      || FIELDS.some((f) => draft[f.key] !== stored[f.key]),
    [draft, stored, followed, storedFollowed],
  );

  /** ⚠ Takes a value OR an updater — the hypothesis picker appends to what is
      already in the box, so it needs the current value (TCR-88). */
  const set = (key: keyof RecapPatch) => (v: string | ((cur: string) => string)) =>
    setDraft((d) => ({ ...d, [key]: typeof v === 'function' ? v(d[key]) : v }));

  const revert = useCallback(() => {
    setDraft(patchOf(entry));
    setFollowed(followedOf(entry));
    setError(null);
  }, [entry]);

  /**
   * 🔴 THE BASELINE THIS EDITOR HAS WRITTEN, which is not always the prop. Same
   * reasoning as `PlanEditor` — read the long note there before changing this.
   *
   * `saveJournalRecap` runs the same fail-open stale check as the plan write and
   * refuses anything older than the row's stored `updated_at`. The baseline comes
   * in as a PROP via `onSaved` and the parent, which was fast enough when a human
   * pressed Save and is not fast enough for blur: the second field left would
   * re-send the original timestamp and the trader would be told their own typing
   * was a conflict from another tab.
   */
  const baselineRef = useRef(baselineUpdatedAt);
  useEffect(() => {
    const held = baselineRef.current;
    if (!held
      || (baselineUpdatedAt
        && new Date(baselineUpdatedAt).getTime() > new Date(held).getTime())) {
      baselineRef.current = baselineUpdatedAt;
    }
  }, [baselineUpdatedAt]);

  /** ⚠ ONE WRITE AT A TIME — see `PlanEditor`. A blur mid-write queues one retry. */
  const inFlight = useRef(false);
  const queued = useRef(false);

  const save = useCallback(async () => {
    if (inFlight.current) { queued.current = true; return; }
    inFlight.current = true;
    setSaving(true);
    setError(null);

    // ⚠ Built from the entry's OWN booleans, not from a fresh object. `booleans`
    // also carries the swap marker and any key this form does not render, and
    // replacing it would drop them — the marker most of all, whose loss re-inverts
    // the trader's self-review on the next load in either client.
    const booleans: Record<string, unknown> = { ...entry.booleans };
    // ⚠ `null` DELETES the key rather than storing null. The old client reads
    // `undefined` as "not stated"; a stored null would be a third value it has
    // never seen, and `v === false` is what it tests for "No".
    if (followed === null) delete booleans[FOLLOWED];
    else booleans[FOLLOWED] = followed;

    const r = await saveJournalRecap({
      sb, userId, date, patch: draft, booleans, baselineUpdatedAt: baselineRef.current,
    });
    inFlight.current = false;
    setSaving(false);

    if (r.error) { setError(r.error); return; }

    // Fold the saved values back in so the surface and the store agree without a
    // 700-row reload.
    onSaved(
      { ...entry, eodRecap: { ...entry.eodRecap, ...draft }, booleans },
      r.updatedAt,
    );
    if (r.updatedAt) baselineRef.current = r.updatedAt;
    setSavedAt(new Date().toLocaleTimeString());
    if (queued.current) { queued.current = false; void save(); }
  }, [sb, userId, date, draft, followed, entry, onSaved]);

  /**
   * ⚠ Autosave writes on blur and flushes before the panel is torn down. The
   * Save button below stays as reassurance, not as the save path — Josh:
   * "One button at the bottom is fine to give a user something to click."
   */
  const autosave = useAutosave({ dirty, commit: save });

  /**
   * This morning's hypotheses, for the two setup boxes (TCR-88).
   *
   * ✅ ALREADY IN HAND — no new read and no new prop. `entry.preMarketPlan` is
   * on the entry this editor is given. Threading anything through `Journal.tsx`
   * for this would be inventing a pipe that already exists.
   */
  const offered = useMemo(() => offerableHypotheses(entry.preMarketPlan), [entry.preMarketPlan]);

  /**
   * 🔴 THE FLUSH HAS TO HAPPEN AFTER THE DRAFT CHANGES, NOT IN THE CLICK.
   *
   * `commit` closes over `draft`, and `dirty` is computed from it. Calling
   * `autosave.flush()` inside the chip's handler would run against the draft
   * from BEFORE the append — writing the previous text, or nothing at all when
   * the box was clean. So the click only raises a flag, and this effect fires
   * once the render carrying the new draft has committed.
   *
   * ⚠ Deliberately not keyed on `dirty`: a pick that re-adds a line already
   * present is a no-op on the draft, and the flag must still clear.
   */
  const [pendingFlush, setPendingFlush] = useState(false);
  useEffect(() => {
    if (!pendingFlush) return;
    setPendingFlush(false);
    autosave.flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFlush, draft]);

  /** ⚠ Hidden units, applied as a CLASS only — see `JournalSection` (TCR-81). */
  const { hidden: hiddenUnits } = useJournalHidden();

  return (
    <div className="jp-recap-editor">
      {/* ── 🚨 THE 2×2 PAIRING IS INFORMATION, NOT DECORATION ──
          The old client lays the first four fields out as a grid — POSITIVE in the
          left column, NEGATIVE in the right — and that alignment is the only thing
          on screen that makes "did well" vs "need to fix" obvious at a glance.
          Stacking them in one column, as the first port did, removes it. Josh
          reversed those two fields himself on an earlier entry, which is precisely
          the mistake the grid exists to prevent. Ported for that reason, not to
          look closer to beta. */}
      <div className="form-grid recap-grid">
        {FIELDS.map((f) => (
          /* 🔴 `recap-col-l` / `recap-col-r` PIN THE FOUR PAIRED FIELDS TO A
             COLUMN, and that is a hide requirement rather than a layout nicety.
             `.recap-grid` is `1fr 1fr` with AUTO-PLACEMENT, so hiding "Things I
             did well" would slide "Things I need to fix" into the LEFT column —
             where the positive field belongs. The comment above records why that
             matters: the alignment is the only thing making the two obvious at a
             glance, and Josh reversed exactly those two on a real entry. An
             explicit column means a survivor cannot drift (TCR-81).

             🔴 AND THE SURVIVOR SPANS, BUT ONLY THEN (TCR-83). Josh asked for the
             remaining field to widen into the gap. ⚠ It does NOT contradict the
             pinned columns above — it completes them. The pins stop a survivor
             DRIFTING into the other half of the pair while both are on screen,
             which is what once had "did well" and "need to fix" filled in
             backwards. With one of a pair hidden there is no left-versus-right
             left to misread, so spanning is safe exactly where pinning stopped
             being needed.

             🔴 THIS PARAGRAPH USED TO LIVE INSIDE THE `className={…}` EXPRESSION
             BELOW, AND THAT BROKE `check:styles` FOR TWO DAYS. The guard pulls
             string literals out of that expression without stripping comments,
             so the apostrophe in "partner's" opened a literal and every word
             after it was reported as an unstyled class — 78 of them, `.a`,
             `.and`, `.at`. ⚠ CI does not run that guard, which is why it shipped
             silently. ▶ Keep prose about a className ABOVE the element, never
             inside the expression. */
          <div
            /* ⚠ The span reasoning is in the block above, NOT in here. A comment
               inside a `className={…}` expression broke `check:styles` for two
               days — see that block's last paragraph. */
            className={`field${f.wide ? ' recap-wide' : ''}${f.focus ? ' recap-focus' : ''}`
              + (f.wide ? '' : f.col === 'l' ? ' recap-col-l' : ' recap-col-r')
              + (!f.wide && f.pair && hiddenUnits.includes(`recap:${f.pair}`)
                && !hiddenUnits.includes(`recap:${f.key}`) ? ' recap-span-2' : '')
              + (hiddenUnits.includes(`recap:${f.key}`) ? ' jp-hidden' : '')}
            data-hide-id={`recap:${f.key}`}
            key={f.key}
          >
            <label className="field-label" htmlFor={`eod-${f.key}`}>{f.label}</label>
            {/* 🔴 INSIDE THIS DIV, NOT BESIDE IT. The wrapper carries
                `data-hide-id` and gets `jp-hidden` when the trader hides this
                field (TCR-81/82) — a picker rendered outside it would outlive
                its own box and offer hypotheses with nowhere to put them. */}
            {f.hypotheses ? (
              <HypothesisPicker
                field={f.key}
                offered={offered}
                onPick={(line) => {
                  set(f.key)((cur) => appendHypothesis(cur, line));
                  // 🔴 `flush`, NOT `onBlur`. A chip is not a text field: clicking
                  // one may never blur the textarea, so the pick would sit in the
                  // draft until something else happened to commit — and a tab
                  // switch would take it. This is the defect that cost Josh a
                  // morning (TCR-79), and `useAutosave` exposes `flush` saying in
                  // as many words that it is "for a control that commits without
                  // blurring".
                  //
                  // ⚠ AFTER the state update, in an effect — see `pendingFlush`.
                  setPendingFlush(true);
                }}
              />
            ) : null}
            <textarea
              id={`eod-${f.key}`}
              rows={f.rows}
              value={draft[f.key]}
              onChange={(e) => set(f.key)(e.target.value)}
              onBlur={autosave.onBlur}
            />
          </div>
        ))}

        {/* ⚠ THREE-STATE. See the header: a checkbox would write "No" for every
            day the user saved without touching it, and the old client renders that
            as a red "Plan Followed: No" in its glance band. */}
        <fieldset className="field ann-breaches">
          <legend className="field-label">
            Followed the pre-market plan today?{' '}
            <span className="muted">— leave unanswered if you’d rather not say; it
            shows as “—” rather than as a No.</span>
          </legend>
          {([
            [true, 'Yes'],
            [false, 'No'],
            [null, 'Not stated'],
          ] as const).map(([v, label]) => (
            <label className="filter-opt" key={String(v)}>
              <input
                type="radio"
                name="eod-followed"
                checked={followed === v}
                onChange={() => setFollowed(v)}
              />
              <span className="filter-opt-label">{label}</span>
            </label>
          ))}
        </fieldset>
      </div>

      {error ? (
        <div role="alert" className="import-alert">
          <b>Nothing was saved.</b> {error}
        </div>
      ) : null}

      <div className="import-row">
        <button
          type="button"
          className="btn primary"
          disabled={saving || !dirty}
          onClick={() => void save()}
        >
          {saving ? 'Saving…' : 'Save recap'}
        </button>
        <button type="button" className="btn" disabled={saving || !dirty} onClick={revert}>
          Revert
        </button>
        {/* ⚠ No autosave, and it is stated. The old client saves this form on an
            explicit action too, and two clients with different save timing on the
            same row is a lost-edit generator. */}
        <span className="muted">
          {dirty ? 'Unsaved changes' : savedAt ? `Saved at ${savedAt}` : 'No changes'}
        </span>
      </div>
    </div>
  );
}

/**
 * This morning's hypotheses, offered as chips above a setup box (TCR-88).
 *
 * ⚠ NO HYPOTHESES IS THE NORMAL CASE, and it says so in words rather than
 * rendering an empty control. Josh's sentence covers it directly: *"The free
 * text field should also remain in case hypos were not written."* ▶ The textarea
 * is never hidden and never disabled by this — whatever is above it.
 *
 * ⚠ A hypothesis carrying neither a setup nor a named pattern is not offered at
 * all; `offerableHypotheses` drops it. The plan editor keeps blank rows while
 * the trader is still thinking, and a chip with no label is worse than no chip.
 */
function HypothesisPicker(
  { field, offered, onPick }: {
    field: string;
    offered: readonly { index: number; line: string }[];
    onPick: (line: string) => void;
  },
) {
  if (!offered.length) {
    return (
      <p className="muted recap-hypo-none">
        No hypotheses were written this morning — write the setups here instead.
      </p>
    );
  }
  return (
    <div className="recap-hypo" role="group" aria-label="This morning’s hypotheses">
      <span className="muted recap-hypo-k">From this morning’s plan</span>
      {offered.map((h) => (
        <button
          key={`${field}-${h.index}`}
          type="button"
          className="period-chip recap-hypo-chip"
          // ⚠ The line itself is the label. A numbered chip would make the
          // trader open the Pre-Market plan to find out which one they are
          // about to add.
          onClick={() => onPick(h.line)}
        >{h.line}</button>
      ))}
    </div>
  );
}
